import { useEffect, useMemo, useState } from "react";
import { useBasketWishlist } from "../../../context/BasketWishlistContext";
import { useCurrencies } from "../../../context/CurrenciesContext";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { Address } from "../../../api/types/products.types";
import LocationService from "../../../api/services/locations.service";
import Select from "../../../components/common/select/Select";
import { useTranslation } from "react-i18next";
import { useLocalizedNavigate } from "../../../hooks/useLocalizedNavigate";
import styles from "./CompleteOrder.module.scss";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import Button from "../../../components/common/button/Button";
import { toast } from "sonner";
import ProductsService from "../../../api/services/products.service";
import Loading from "../../../components/common/loading/Loading";
import Input from "../../../components/common/input/Input";
import MapLocationPicker from "../../../components/common/map-location-picker/MapLocationPicker";
import Modal from "../../../components/common/modal/Modal";
import { useBasketDelivery } from "../../../hooks/useBasketDelivery";
import { useInstallmentBanks } from "../../../hooks/useInstallmentBanks";
import InstallmentSummary, {
  InstallmentSelection,
} from "../../../components/sections/basket/installment-summary/InstallmentSummary";
import {
  readInstallmentSelection,
  writeInstallmentSelection,
} from "../../../utils/installmentSelection";
import Visa from "../../../assets/images/basket/VİSA.png";
import ApplePay from "../../../assets/images/basket/ApplePay.png";
import GooglePay from "../../../assets/images/basket/GooglePay.png";

interface City {
  id: number;
  name: string;
}

interface Country {
  id: number;
  name: string;
}

interface AddressPayload {
  city_id: number | null;
  address: string;
  phone: string;
  zip_code: string;
  latitude?: number | null;
  longitude?: number | null;
}

const CompleteOrder = () => {
  const { basketTotalPrice, basketCount } = useBasketWishlist();
  const { currencyId, currency } = useCurrencies();
  const { delivery } = useBasketDelivery(currencyId);
  const deliveryFee = delivery?.delivery_fee ?? 0;
  // The backend charges products plus delivery, so the amount submitted has to
  // include the fee or the payment is rejected as an invalid price.
  const orderTotal = basketTotalPrice + deliveryFee;
  const installmentBanks = useInstallmentBanks();
  const [installment, setInstallment] = useState<InstallmentSelection | null>(
    null,
  );
  // An instalment term with a markup makes the order cost more, and the
  // backend works the same figure out from the plan id before charging
  // anything — so what is submitted here has to match or it is rejected.
  const markupPercent = installment?.term.markup_percent ?? 0;
  const installmentFee =
    Math.round(orderTotal * (markupPercent / 100) * 100) / 100;
  const amountToPay = orderTotal + installmentFee;
  const { t } = useTranslation();
  const [addressId, setAddressId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const navigate = useLocalizedNavigate();
  const [newAddress, setNewAddress] = useState<Partial<AddressPayload>>({
    city_id: null,
    address: "",
    phone: "",
    zip_code: "",
    latitude: null,
    longitude: null,
  });
  const [countryId, setCountryId] = useState<number | null>(null);
  const [errors, setErrors] = useState<
    Partial<Record<keyof AddressPayload, string>>
  >({});
  const [widgetUrl, setWidgetUrl] = useState<string | null>(null);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [mapCenter, setMapCenter] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const widgetPaymentOrigin = useMemo(() => {
    if (!widgetUrl) return "https://epoint.az";
    try {
      return new URL(widgetUrl).origin;
    } catch {
      return "https://epoint.az";
    }
  }, [widgetUrl]);

  const {
    data: addresses,
    loading: addressLoading,
    reset: refetchAddresses,
  } = useInfiniteScroll<Address>({
    fetchFn: async (page) => {
      const res = await LocationService.getAll(page, 20);
      return res.data.data;
    },
    pageSize: 20,
  });

  const { data: countries } = useInfiniteScroll<Country>({
    fetchFn: async (page) => {
      const res = await LocationService.countries(page, 20);
      return res?.data?.data;
    },
    pageSize: 20,
  });

  const { data: cities } = useInfiniteScroll<City>({
    fetchFn: async (page) => {
      if (!countryId) return [];
      const res = await LocationService.cities(Number(countryId), page, 20);
      return res?.data?.data;
    },
    pageSize: 20,
    enabled: Boolean(countryId),
    deps: [countryId],
  });

  // The plan picked in the basket, carried over so it is not asked for twice.
  useEffect(() => {
    const stored = readInstallmentSelection();
    if (!stored || !installmentBanks.length) return;

    const bank = installmentBanks.find((b) => b.id === stored.bankId);
    const term = bank?.terms.find((t) => t.id === stored.termId);
    if (bank && term) setInstallment({ bank, term });
  }, [installmentBanks]);

  const chooseInstallment = (selection: InstallmentSelection | null) => {
    setInstallment(selection);
    writeInstallmentSelection(
      selection
        ? { bankId: selection.bank.id, termId: selection.term.id }
        : null,
    );
  };

  const handleSubmit = async () => {
    if (!addressId) {
      toast.error(t("complete_order.errors.select_address"));
      return;
    }
    if (!currencyId) {
      toast.error(t("complete_order.errors.currency_required"));
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      const priceToPay = Math.round(amountToPay * 100) / 100;
      formData.append("price", priceToPay.toFixed(2));
      formData.append("address_id", String(addressId));
      formData.append("currency_id", String(currencyId));
      if (installment) {
        formData.append("installment_term_id", String(installment.term.id));
      }
      const res = await ProductsService.completeOrderAndPay(formData);
      if (res.data.status) {
        // The plan has been handed to the payment; leaving it in session
        // storage would quietly attach it to whatever is bought next.
        writeInstallmentSelection(null);
        window.location.href = res.data?.redirect_url;
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          t("common_error"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleWidgetPay = async () => {
    if (installment) {
      toast.error(t("installments.card_only"));
      return;
    }

    if (!addressId) {
      toast.error(t("complete_order.errors.select_address"));
      return;
    }
    if (!currencyId) {
      toast.error(t("complete_order.errors.currency_required"));
      return;
    }

    setWidgetLoading(true);
    try {
      const formData = new FormData();
      const priceToPay = Math.round(amountToPay * 100) / 100;
      formData.append("price", priceToPay.toFixed(2));
      formData.append("address_id", String(addressId));
      formData.append("currency_id", String(currencyId));
      const res = await ProductsService.createWidgetPayment(formData);
      if (res.data?.widget_url) {
        setWidgetUrl(res.data.widget_url);
        setShowWidgetModal(true);
      } else {
        toast.error(t("common_error"));
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          t("common_error"),
      );
    } finally {
      setWidgetLoading(false);
    }
  };

  // Listen for postMessage from Epoint widget iframe
  useEffect(() => {
    if (!widgetUrl) return;

    let allowedOrigin: string | null = null;
    try {
      allowedOrigin = new URL(widgetUrl).origin;
    } catch {
      allowedOrigin = null;
    }

    const handler = (event: MessageEvent) => {
      if (!event?.data || typeof event.data !== "object") return;
      if (!allowedOrigin || event.origin !== allowedOrigin) return;

      const status = event.data.status;
      if (status === "success") {
        window.location.href = "/payment/success";
      } else if (status === "error" || status === "failed") {
        toast.error(t("error_subtitle"));
        window.location.href = "/payment/error";
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [t, widgetUrl]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setNewAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name as keyof AddressPayload]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (name: string, value: string | number) => {
    setNewAddress((prev) => ({
      ...prev,
      [name]:
        name === "city_id" ? (value === "" ? null : Number(value)) : value,
    }));
    if (errors[name as keyof AddressPayload]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (name === "city_id" && value) {
      const selectedCity = cities?.find((city) => city.id === Number(value));
      if (selectedCity) {
        setMapCenter({ latitude: 40.4093, longitude: 49.8671 });
      }
    }
  };

  const handleCountryChange = (value: { id: string; name: string }) => {
    const countryId = Number(value.id);
    setCountryId(countryId);
    setNewAddress((prev) => ({ ...prev, city_id: null }));
    setMapCenter(null);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for "Use current location" button
  const handleUseCurrentLocation = () => {
    if (!countryId || !newAddress.city_id) {
      toast.error(
        t("complete_order.errors.select_country_city") ||
          "Please select country and city first",
      );
      return;
    }

    if (!navigator.geolocation) {
      toast.error(
        t("complete_order.errors.geolocation_not_supported") ||
          "Geolocation is not supported by your browser",
      );
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setNewAddress((prev) => ({
          ...prev,
          latitude,
          longitude,
        }));
        setMapCenter({ latitude, longitude });
        toast.success(
          t("complete_order.location_detected") ||
            "Current location detected successfully",
        );
      },
      (error) => {
        console.error("Geolocation error:", error);
        toast.error(
          t("complete_order.errors.location_access_denied") ||
            "Location access denied. Please enable location permissions.",
        );
      },
    );
  };

  const validateForm = () => {
    let newErrors: Partial<Record<keyof AddressPayload, string>> = {};
    if (!newAddress?.address?.trim()) {
      newErrors.address = t("my_locations.errors.addressRequired") || "";
    }
    if (!newAddress?.zip_code?.trim()) {
      newErrors.zip_code = t("my_locations.errors.zipCodeRequired") || "";
    }
    if (newAddress?.city_id == null) {
      newErrors.city_id = t("my_locations.errors.cityRequired") || "";
    }
    if (!newAddress?.phone?.trim()) {
      newErrors.phone = t("my_locations.errors.phoneRequired") || "";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddAddress = async () => {
    if (!validateForm()) return;

    setIsLoadingAddresses(true);
    try {
      const formData = new FormData();
      formData.append("city_id", newAddress.city_id?.toString() || "");
      formData.append("address", newAddress.address || "");
      formData.append("phone", newAddress.phone || "");
      formData.append("zip_code", newAddress.zip_code || "");
      if (newAddress.latitude != null) {
        formData.append("latitude", newAddress.latitude.toString());
      }
      if (newAddress.longitude != null) {
        formData.append("longitude", newAddress.longitude.toString());
      }

      await LocationService.create(formData);
      await refetchAddresses();
      setNewAddress({
        city_id: null,
        address: "",
        phone: "",
        zip_code: "",
        latitude: null,
        longitude: null,
      });
      setCountryId(null);
      setErrors({});
      toast.success(
        t("my_locations.address_added_success") || "Ünvan uğurla əlavə edildi",
      );
    } catch (error: any) {
      console.error(error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        t("common_error");
      toast.error(errorMessage);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const selectedAddress = addresses?.find(
    (addr) => Number(addr.id) === Number(addressId),
  );

  if (addressLoading) {
    return <Loading />;
  }

  return (
    <div className="container">
      <div className={styles.complete_order}>
        <TitleUpdater title={t("complete_order.title")} />
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className={styles.title}>{t("complete_order.title")}</h1>
            <p className={styles.subtitle}>{t("complete_order.subtitle")}</p>
          </div>

          <div className={styles.main_content}>
            <div className={styles.left_section}>
              <div className={styles.shipping_section}>
                <h2 className={styles.section_title}>
                  {t("complete_order.shipping_address")}
                </h2>

                <div className={styles.address_form}>
                  <div className={styles.form}>
                    <div className={styles.form_row}>
                      <div className={styles.form_group}>
                        <Select
                          label={t("my_locations.form.country") || ""}
                          options={countries?.map((country) => ({
                            id: String(country.id),
                            name: country.name,
                          }))}
                          value={countryId?.toString() || ""}
                          onChange={handleCountryChange}
                        />
                      </div>
                      <div className={styles.form_group}>
                        <Select
                          label={t("my_locations.form.city") || ""}
                          options={cities?.map((city) => ({
                            id: String(city.id),
                            name: city.name,
                          }))}
                          value={newAddress.city_id?.toString() || ""}
                          onChange={(value) =>
                            handleSelectChange("city_id", value.id)
                          }
                          disabled={!countryId}
                          error={errors.city_id}
                        />
                      </div>
                    </div>

                    <div className={styles.form_group}>
                      <Input
                        label={t("my_locations.form.address") || ""}
                        placeholder={
                          t("my_locations.form.placeholders.address") || ""
                        }
                        type="text"
                        name="address"
                        value={newAddress.address}
                        onChange={handleInputChange}
                        disabled={isLoadingAddresses}
                        error={errors.address}
                      />
                    </div>

                    <div className={styles.form_row}>
                      <div className={styles.form_group}>
                        <Input
                          phone
                          label={t("my_locations.form.phone_number") || ""}
                          type="tel"
                          name="phone"
                          value={newAddress.phone}
                          onChange={handleInputChange}
                          placeholder="(XXX) XXX-XXXX"
                          disabled={isLoadingAddresses}
                          error={errors.phone}
                        />
                      </div>
                      <div className={styles.form_group}>
                        <Input
                          label={t("my_locations.form.zip_code") || ""}
                          placeholder={
                            t("my_locations.form.placeholders.zip_code") || ""
                          }
                          type="text"
                          name="zip_code"
                          value={newAddress.zip_code}
                          onChange={handleInputChange}
                          disabled={isLoadingAddresses}
                          error={errors.zip_code}
                        />
                      </div>
                    </div>

                    <div className={styles.form_actions}>
                      <Button
                        onClick={handleAddAddress}
                        loading={isLoadingAddresses}
                        disabled={isLoadingAddresses}
                        className={styles.submit_address_btn}
                      >
                        {t("my_locations.add_new_address")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.right_section}>
              {addresses && addresses.length > 0 && (
                <div className={styles.address_selection}>
                  <h3 className={styles.selection_title}>
                    {t("complete_order.select_delivery_address") ||
                      "Select Delivery Address"}
                  </h3>

                  <div className={styles.address_search_header}>
                    <Select
                      label={""}
                      options={addresses.map((address) => ({
                        id: String(address.id),
                        name: address.address,
                      }))}
                      className={styles.search_input}
                      value={addressId?.toString() || ""}
                      onChange={(value) => setAddressId(Number(value.id))}
                      placeholder={t("complete_order.select") || "Select..."}
                    />
                  </div>
                </div>
              )}

              {/* Map Container */}
              <div className={styles.map_container}>
                {selectedAddress ? (
                  selectedAddress.latitude && selectedAddress.longitude ? (
                    <MapLocationPicker
                      latitude={selectedAddress.latitude}
                      longitude={selectedAddress.longitude}
                      address={selectedAddress.address}
                      cityName={selectedAddress.city?.name || null}
                      countryName={null}
                      onLocationChange={() => {}}
                      disabled={true}
                    />
                  ) : (
                    <div className={styles.map_placeholder}>
                      {t("complete_order.map_view") || "Map view"}
                    </div>
                  )
                ) : mapCenter ||
                  (newAddress.latitude && newAddress.longitude) ? (
                  <MapLocationPicker
                    latitude={
                      newAddress.latitude || mapCenter?.latitude || 40.4093
                    }
                    longitude={
                      newAddress.longitude || mapCenter?.longitude || 49.8671
                    }
                    address={newAddress.address || ""}
                    cityName={
                      cities?.find((c) => c.id === newAddress.city_id)?.name ||
                      null
                    }
                    countryName={
                      countries?.find((c) => c.id === countryId)?.name || null
                    }
                    onLocationChange={(lat, lng) => {
                      setNewAddress((prev) => ({
                        ...prev,
                        latitude: lat,
                        longitude: lng,
                      }));
                    }}
                    disabled={false}
                  />
                ) : (
                  <div className={styles.map_placeholder}>
                    {t("complete_order.select_location_to_view_map") ||
                      "Select country and city to view map"}
                  </div>
                )}
              </div>

              <div className={styles.order_summary}>
                <h2 className={styles.section_title}>
                  {t("complete_order.order_summary") || "Order Summary"}
                </h2>
                <div className={styles.summary_card}>
                  <div className={styles.summary_row}>
                    <span className={styles.label}>
                      {t("complete_order.products") || "Products:"}:
                    </span>
                    <span className={styles.value}>
                      {t("complete_order.products_count", {
                        count: basketCount,
                      }) || `${basketCount} products`}
                    </span>
                  </div>
                  <div className={styles.summary_row}>
                    <span className={styles.label}>
                      {t("complete_order.amount") || "Amount:"}:
                    </span>
                    <span className={styles.value}>
                      {(Math.round(basketTotalPrice * 100) / 100).toFixed(2)}{" "}
                      {currency}
                    </span>
                  </div>

                  {delivery && delivery.delivery_fee_full > 0 && (
                    <>
                      <div className={styles.summary_row}>
                        <span className={styles.label}>
                          {t("complete_order.delivery") || "Delivery"}:
                        </span>
                        <span className={styles.value}>
                          {delivery.is_free_delivery
                            ? t("complete_order.delivery_free") || "Free"
                            : `${delivery.delivery_fee.toFixed(2)} ${delivery.currency_name}`}
                        </span>
                      </div>

                      {delivery.free_delivery_threshold > 0 &&
                        !delivery.is_free_delivery && (
                          <p className={styles.delivery_note}>
                            {t("complete_order.delivery_hint", {
                              amount:
                                delivery.free_delivery_threshold.toFixed(2),
                              currency: delivery.currency_name,
                            })}
                          </p>
                        )}
                    </>
                  )}

                  <InstallmentSummary
                    banks={installmentBanks}
                    total={orderTotal}
                    currency={currency}
                    selection={installment}
                    onSelect={chooseInstallment}
                  />

                  {installment && installmentFee > 0 && (
                    <div className={styles.summary_row}>
                      <span className={styles.label}>
                        {t("installments.fee", { percent: markupPercent })}:
                      </span>
                      <span className={styles.value}>
                        {installmentFee.toFixed(2)} {currency}
                      </span>
                    </div>
                  )}

                  <div className={styles.summary_row}>
                    <span className={styles.label}>
                      {t("complete_order.total") || "Total"}:
                    </span>
                    <span className={styles.total_price}>
                      {(Math.round(amountToPay * 100) / 100).toFixed(2)}{" "}
                      {currency}
                    </span>
                  </div>

                  {installment && (
                    <p className={styles.delivery_note}>
                      {t("installments.bank_note", {
                        bank: installment.bank.name,
                        months: installment.term.months,
                        amount: (
                          Math.round(
                            (amountToPay / installment.term.months) * 100,
                          ) / 100
                        ).toFixed(2),
                        currency,
                      })}
                    </p>
                  )}
                </div>
              </div>

              {/* Payment Methods */}
              <div className={styles.payment_methods}>
                <h2 className={styles.section_title}>
                  {t("complete_order.payment_methods") || "Payment Methods"}
                </h2>
                <div className={styles.payment_buttons}>
                  <button
                    className={styles.payment_btn}
                    onClick={handleSubmit}
                    disabled={!addressId || loading}
                  >
                    <div className={styles.payment_icon}>
                      <img src={Visa} alt="Visa" />
                    </div>
                    <div className={styles.payment_label}>
                      {t("complete_order.credit_card") || "Credit card"}
                    </div>
                  </button>
                  <button
                    className={styles.payment_btn}
                    onClick={handleWidgetPay}
                    disabled={!addressId || widgetLoading || !!installment}
                    title={
                      installment
                        ? (t("installments.card_only") as string)
                        : undefined
                    }
                  >
                    <div className={styles.payment_icon}>
                      <img src={ApplePay} alt="Apple Pay" />
                    </div>
                    <div className={styles.payment_label}>
                      {t("complete_order.apple_pay") || "Apple Pay"}
                    </div>
                  </button>
                  <button
                    className={styles.payment_btn}
                    onClick={handleWidgetPay}
                    disabled={!addressId || widgetLoading || !!installment}
                    title={
                      installment
                        ? (t("installments.card_only") as string)
                        : undefined
                    }
                  >
                    <div className={styles.payment_icon}>
                      <img src={GooglePay} alt="Google Pay" />
                    </div>
                    <div className={styles.payment_label}>
                      {t("complete_order.google_pay") || "Google Pay"}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.back_to_basket}>
            <button
              onClick={() => navigate("/basket")}
              className={styles.back_btn}
            >
              ← {t("complete_order.back_to_basket")}
            </button>
          </div>
        </div>
      </div>
      <Modal
        isOpen={showWidgetModal}
        onClose={() => setShowWidgetModal(false)}
        title={t("complete_order.apple_google_title") || ""}
        footer={false}
      >
        {widgetUrl ? (
          <iframe
            src={widgetUrl}
            title="Epoint Apple Pay / Google Pay"
            style={{ width: "100%", height: "600px", border: "none" }}
            allow={`payment ${widgetPaymentOrigin}; fullscreen`}
          />
        ) : (
          <Loading />
        )}
      </Modal>
    </div>
  );
};

export default CompleteOrder;
